"use client";

import type { LucideIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { RecentlyViewed } from "@/components/product/recently-viewed";
import { FEATURE_ICONS, resolveFeatureIcon } from "@/components/home/feature-cards";
import { Headphones, RotateCcw, ShieldCheck, Truck } from "lucide-react";

interface TrustCard {
  Icon: LucideIcon;
  title: string;
  text: string;
}

function buildTrustCards(cfg: Record<string, string>): TrustCard[] {
  const fallbacks: { ic: LucideIcon; title: string; text: string }[] = [
    { ic: Truck, title: "Hızlı Teslimat", text: "Siparişleriniz özenle hazırlanır." },
    { ic: ShieldCheck, title: "Güvenli Alışveriş", text: "Ödeme bilgileriniz korunur." },
    { ic: RotateCcw, title: "Kolay İade", text: "İade sürecini hızlıca başlatın." },
    { ic: Headphones, title: "Destek", text: "İhtiyacınız olduğunda yanınızdayız." },
  ];
  return fallbacks.map((f, i) => {
    const n = i + 1;
    return {
      Icon: resolveFeatureIcon(cfg[`theme.trust${n}_icon`], f.ic),
      title: cfg[`theme.trust${n}_title`] || f.title,
      text: cfg[`theme.trust${n}_desc`] || f.text,
    };
  });
}

export function RecentlyViewedFooterSection({
  layoutClass,
  themeConfig = {},
}: {
  layoutClass: string;
  themeConfig?: Record<string, string>;
}) {
  const pathname = usePathname();
  // Ürün detay sayfasında, o an görüntülenen ürünü listeden çıkar
  const currentProductSlug = pathname?.startsWith("/products/")
    ? decodeURIComponent(pathname.slice("/products/".length).split("/")[0])
    : undefined;

  const trustEnabled = themeConfig["theme.trust_enabled"] !== "false";
  const trustCards = buildTrustCards(themeConfig);
  const iconColor = themeConfig["theme.trust_icon_color"] || "var(--sf-accent)";
  const iconBg =
    themeConfig["theme.trust_icon_bg"] || "color-mix(in srgb, var(--sf-accent) 10%, transparent)";
  const titleColor = themeConfig["theme.trust_title_color"] || "var(--sf-foreground)";
  const descColor = themeConfig["theme.trust_desc_color"] || "var(--es-text-muted)";

  const cardInner = (card: TrustCard) => {
    const { Icon } = card;
    return (
      <>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md" style={{ backgroundColor: iconBg, color: iconColor }}>
          <Icon className="h-5 w-5" />
        </span>
        <span>
          <span className="block text-sm font-bold" style={{ color: titleColor }}>{card.title}</span>
          <span className="mt-1 block text-xs leading-5" style={{ color: descColor }}>{card.text}</span>
        </span>
      </>
    );
  };

  return (
    <section className="border-t border-[var(--es-border-soft)] bg-[color-mix(in_srgb,var(--sf-foreground)_2%,transparent)]">
      <div className={`${layoutClass} py-6 sm:py-10`}>
        <RecentlyViewed excludeSlug={currentProductSlug} className="mb-6 mt-0 border-t-0 pt-0 sm:mb-10" />

        {trustEnabled && (
          <>
            {/* Mobil: otomatik kayan şerit (hover/dokunuşta durur) */}
            <div className="group -mx-4 overflow-hidden px-4 sm:hidden">
              <div className="flex w-max gap-3 animate-marquee group-hover:[animation-play-state:paused] group-active:[animation-play-state:paused]">
                {[...trustCards, ...trustCards].map((card, i) => (
                  <div
                    key={i}
                    aria-hidden={i >= trustCards.length}
                    className="flex w-[68vw] shrink-0 items-start gap-3 rounded-lg border border-[var(--es-border-soft)] bg-[var(--sf-card-bg,var(--es-surface,#fff))] p-4 shadow-[var(--es-shadow-sm)]"
                  >
                    {cardInner(card)}
                  </div>
                ))}
              </div>
            </div>
            {/* Masaüstü: grid */}
            <div className="hidden gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-4">
              {trustCards.map((card, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-lg border border-[var(--es-border-soft)] bg-[var(--sf-card-bg,var(--es-surface,#fff))] p-4 shadow-[var(--es-shadow-sm)]"
                >
                  {cardInner(card)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
